import { useState, useEffect, useRef } from 'preact/hooks'
import { Users, Plus, Trash2, Shield, Settings, Eye, Edit, Search, ChevronDown, Building2, Check } from 'lucide-preact'
import { useAuth } from '../../../auth/useAuth'
import { useUserProfile, USER_ROLES } from '../../../hooks/useUserProfile'
import { userRolesApi } from '../../../api/userRoles'
import { UserRoleEditModal } from '../../modals/UserRoleEditModal'
import { UserCreateModal } from '../../modals/UserCreateModal'
import { ConfirmationModal } from '../../modals/ConfirmationModal'
import { PersonAvatar } from '../../common/Avatar'
import type { ClientCompanyViewDto, ClientWorkspaceViewDto } from '@/types/company'
import type { UserProfileWithRolesDto, RoleType, WorkspaceRolesViewDto } from '@/types/userRoles'

interface UserManagementProps {
  selectedCompany: ClientCompanyViewDto
}

// Role icons and colors mapping
const ROLE_CONFIG = {
  Viewer: {
    icon: Eye,
    textColor: 'text-slate-500 dark:text-slate-400',
    overriddenTextColor: 'text-slate-700 dark:text-white',
    bgColor: 'bg-slate-100 dark:bg-slate-700'
  },
  Editor: {
    icon: Edit,
    textColor: 'text-amber-600 dark:text-amber-400',
    overriddenTextColor: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/20'
  },
  Admin: {
    icon: Shield,
    textColor: 'text-red-600 dark:text-red-400',
    overriddenTextColor: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/20'
  }
} as const

// Role badge component
function RoleBadge({ role, isOverridden = false }: { role: RoleType; isOverridden?: boolean }) {
  const config = ROLE_CONFIG[role]
  const IconComponent = config.icon

  // Get the appropriate text color class
  const getTextColorClass = () => {
    if (isOverridden && role === 'Viewer') {
      return config.overriddenTextColor
    }
    return config.textColor
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${config.bgColor} border border-slate-200 dark:border-slate-600 ${isOverridden ? 'opacity-90' : ''
        } ${getTextColorClass()}`}
    >
      <IconComponent className="w-3 h-3" />
      {role}
      {isOverridden && (
        <span className="ml-1 text-xs opacity-75">*</span>
      )}
    </span>
  )
}

// User row component - more compact table-like layout
function UserRow({
  userProfile,
  workspaces,
  onDeleteUser,
  onEditRoles
}: {
  userProfile: UserProfileWithRolesDto
  workspaces: ClientCompanyViewDto['Workspaces']
  onDeleteUser: (userId: string) => void
  onEditRoles: (user: UserProfileWithRolesDto) => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Get role information (may be null if user has no roles assigned)
  const roleSet = userProfile.UserCompanyRoleSet
  const hasCompanyRoles = roleSet && roleSet.CompanyRoles.length > 0
  const hasAnyRoles = roleSet && (roleSet.CompanyRoles.length > 0 || roleSet.WorkspaceRoles.length > 0)

  // Create display name from first and last name
  const displayName = `${userProfile.FirstName} ${userProfile.LastName}`.trim()

  // Get primary roles for summary display
  const getPrimaryRoles = () => {
    if (!roleSet) return []
    if (hasCompanyRoles) return roleSet.CompanyRoles.slice(0, 3) // Show max 3 roles

    // For workspace roles, collect unique roles across all workspaces
    const allWorkspaceRoles = new Set<RoleType>();
    roleSet.WorkspaceRoles.forEach(wsRoles => {
      wsRoles.Roles.forEach(role => allWorkspaceRoles.add(role))
    })
    return Array.from(allWorkspaceRoles).slice(0, 3)
  }

  const primaryRoles = getPrimaryRoles()
  const totalRoles = roleSet ? (hasCompanyRoles ? roleSet.CompanyRoles.length :
    roleSet.WorkspaceRoles.reduce((acc, wsRoles) => acc + wsRoles.Roles.length, 0)) : 0

  return (
    <div className="bg-white dark:bg-slate-600/30 border border-slate-200 dark:border-slate-500 rounded-lg overflow-hidden">
      {/* Main row */}
      <div className="px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-600/50 transition-colors duration-200">
        <div className="flex items-center justify-between">
          {/* User info section */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <PersonAvatar
                firstName={userProfile.FirstName}
                lastName={userProfile.LastName}
                profilePicUrl={userProfile.AvatarUrl}
                alt={displayName}
                size="sm"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h4 className="font-medium text-slate-900 dark:text-white truncate">
                  {displayName || userProfile.UserProfileId}
                </h4>
                <span className="text-sm text-slate-600 dark:text-slate-300 truncate">
                  {userProfile.Email}
                </span>
                {!hasAnyRoles && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded flex-shrink-0">
                    No roles
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Roles section - positioned between user info and actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0 mr-8">
            {primaryRoles.map((role: RoleType) => (
              <RoleBadge key={role} role={role} />
            ))}
            {totalRoles > 3 && (
              <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                +{totalRoles - 3}
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {/* Role management buttons - only show if user has roles */}
            {hasAnyRoles && (
              <>
                <button
                  className="p-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors duration-200"
                  title="Add Role"
                  disabled
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onEditRoles(userProfile)}
                  className="p-1.5 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors duration-200"
                  title="Edit Roles"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors duration-200"
              title={isExpanded ? "Hide details" : "Show details"}
            >
              <Settings className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDeleteUser(userProfile.UserProfileId)}
              className="p-1.5 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors duration-200"
              title="Remove User"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && hasAnyRoles && roleSet && (
        <div className="border-t border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Company-level roles */}
            <div>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Company Roles {hasCompanyRoles && <span className="text-xs text-slate-500">(overrides workspace roles)</span>}
              </div>
              <div className="flex flex-wrap gap-1">
                {roleSet.CompanyRoles.length > 0 ? (
                  roleSet.CompanyRoles.map((role: RoleType) => (
                    <RoleBadge key={role} role={role} />
                  ))
                ) : (
                  <span className="text-sm text-slate-500 dark:text-slate-400">None</span>
                )}
              </div>
            </div>

            {/* Workspace-level roles */}
            <div>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Workspace Roles
              </div>
              {roleSet.WorkspaceRoles.length > 0 ? (
                <div className="space-y-2">
                  {roleSet.WorkspaceRoles.map((wsRoles) => {
                    const workspace = workspaces?.find(d => d.Id === wsRoles.WorkspaceId)
                    return (
                      <div key={wsRoles.WorkspaceId} className="text-sm">
                        <div className="text-slate-800 dark:text-slate-200 font-medium">
                          {workspace?.Name || 'Unknown Workspace'}
                          {hasCompanyRoles && (
                            <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">
                              (overridden by company roles)
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {wsRoles.Roles.map((role: RoleType) => (
                            <RoleBadge key={role} role={role} isOverridden={hasCompanyRoles} />
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <span className="text-sm text-slate-500 dark:text-slate-400">None</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Workspace selector component for user management
interface UserManagementWorkspaceSelectorProps {
  selectedWorkspaceId: string
  onWorkspaceChange: (workspaceId: string) => void
  manageableWorkspaces: ClientWorkspaceViewDto[]
  isCompanyAdmin: boolean
}

function UserManagementWorkspaceSelector({
  selectedWorkspaceId,
  onWorkspaceChange,
  manageableWorkspaces,
  isCompanyAdmin
}: UserManagementWorkspaceSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const selectedWorkspace = selectedWorkspaceId === 'all'
    ? { Name: 'All Workspaces' }
    : manageableWorkspaces.find(ws => ws.Id === selectedWorkspaceId)

  const handleWorkspaceSelect = (workspaceId: string) => {
    onWorkspaceChange(workspaceId)
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
      >
        <Building2 className="w-4 h-4" />
        <span>{selectedWorkspace?.Name || 'Select Workspace'}</span>
        <ChevronDown className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg z-10">
          {/* All Workspaces option for company admins */}
          {isCompanyAdmin && (
            <button
              onClick={() => handleWorkspaceSelect('all')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {selectedWorkspaceId === 'all' && <Check className="w-3 h-3" />}
              </div>
              <Building2 className="w-4 h-4" />
              <span>All Workspaces</span>
            </button>
          )}

          {/* Individual workspaces */}
          {manageableWorkspaces.map(workspace => (
            <button
              key={workspace.Id}
              onClick={() => handleWorkspaceSelect(workspace.Id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {selectedWorkspaceId === workspace.Id && <Check className="w-3 h-3" />}
              </div>
              <Building2 className="w-4 h-4" />
              <span>{workspace.Name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function UserManagement({ selectedCompany }: UserManagementProps) {
  const { user } = useAuth()
  const { getCurrentCompanyRoleSet, hasCompanyRole } = useUserProfile()
  const [userProfiles, setUserProfiles] = useState<UserProfileWithRolesDto[]>([])
  const [filteredProfiles, setFilteredProfiles] = useState<UserProfileWithRolesDto[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('all') // 'all' for All Workspaces
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const userRoles = getCurrentCompanyRoleSet()

  // Modal state
  const [editingUser, setEditingUser] = useState<UserProfileWithRolesDto | null>(null)
  const [isRoleEditModalOpen, setIsRoleEditModalOpen] = useState(false)
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false)

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Load user roles
  useEffect(() => {
    loadUserRoles()
  }, [selectedCompany.Id])

  // Filter users based on search query and selected workspace
  useEffect(() => {
    let filtered = userProfiles

    // First filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(profile => {
        const displayName = `${profile.FirstName} ${profile.LastName}`.toLowerCase()
        const email = profile.Email.toLowerCase()
        const roles = profile.UserCompanyRoleSet ? [
          ...profile.UserCompanyRoleSet.CompanyRoles,
          ...profile.UserCompanyRoleSet.WorkspaceRoles.flatMap(ws => ws.Roles)
        ].join(' ').toLowerCase() : ''

        return displayName.includes(query) || email.includes(query) || roles.includes(query)
      })
    }

    // Then filter by selected workspace
    if (selectedWorkspaceId !== 'all') {
      filtered = filtered.filter(profile => {
        // Show users who have roles in the selected workspace or company-wide roles
        if (!profile.UserCompanyRoleSet) return false

        return profile.UserCompanyRoleSet.CompanyRoles.length > 0 ||
          profile.UserCompanyRoleSet.WorkspaceRoles.some(wsRole => wsRole.WorkspaceId === selectedWorkspaceId)
      })
    }

    setFilteredProfiles(filtered)
  }, [userProfiles, searchQuery, selectedWorkspaceId])

  // Determine which workspaces the current user can manage
  const getManageableWorkspaces = (): ClientWorkspaceViewDto[] => {
    if (!userRoles || !selectedCompany.Workspaces) return []

    // If user is company admin, they can manage all workspaces
    if (userRoles.IsAdmin || hasCompanyRole(USER_ROLES.ADMIN, selectedCompany.Id)) {
      return selectedCompany.Workspaces
    }

    // Otherwise, only show workspaces where user is an admin (using CanAdmin flag)
    const adminWorkspaceIds = userRoles.WorkspaceRoles
      .filter((wsRole: WorkspaceRolesViewDto) => wsRole.CanAdmin)
      .map((wsRole: WorkspaceRolesViewDto) => wsRole.WorkspaceId)

    return selectedCompany.Workspaces.filter(ws => adminWorkspaceIds.includes(ws.Id))
  }

  const manageableWorkspaces = getManageableWorkspaces()
  const isCompanyAdmin = userRoles?.IsAdmin || hasCompanyRole(USER_ROLES.ADMIN, selectedCompany.Id)

  const loadUserRoles = async () => {
    if (!user) return

    setLoading(true)
    setError(null)

    try {
      const response = await userRolesApi.getUserRolesByCompany(selectedCompany.Id)

      if (response.error) {
        setError(response.error)
      } else {
        const profiles = response.data || []
        setUserProfiles(profiles)
        // Initialize filtered profiles when data first loads
        if (!searchQuery.trim()) {
          setFilteredProfiles(profiles)
        }
      }
    } catch (err) {
      console.error('Error loading user roles:', err)
      setError('Failed to load user roles')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = (userId: string) => {
    const userProfile = userProfiles.find(u => u.UserProfileId === userId)
    if (!userProfile) return

    const userName = `${userProfile.FirstName} ${userProfile.LastName}`.trim() || userProfile.Email
    setUserToDelete({ id: userId, name: userName })
    setShowDeleteConfirm(true)
  }

  const confirmDeleteUser = async () => {
    if (!userToDelete) return

    setDeleteLoading(true)
    setError(null)

    try {
      console.log('Deleting user:', userToDelete.id, 'from company:', selectedCompany.Id)

      // Call the delete API
      const response = await userRolesApi.deleteUser(userToDelete.id, selectedCompany.Id)

      if (response.error) {
        setError(response.error)
        return
      }

      // Remove from local state on successful deletion
      setUserProfiles(prev => prev.filter(u => u.UserProfileId !== userToDelete.id))
      setFilteredProfiles(prev => prev.filter(u => u.UserProfileId !== userToDelete.id))

      setShowDeleteConfirm(false)
      setUserToDelete(null)

      console.log('User deleted successfully')
    } catch (err) {
      console.error('Error deleting user:', err)
      setError('Failed to remove user from company')
    } finally {
      setDeleteLoading(false)
    }
  }

  const cancelDeleteUser = () => {
    setShowDeleteConfirm(false)
    setUserToDelete(null)
  }

  const handleEditUserRoles = (user: UserProfileWithRolesDto) => {
    setEditingUser(user)
    setIsRoleEditModalOpen(true)
  }

  const handleRoleEditSuccess = (updatedUser: UserProfileWithRolesDto) => {
    // Update the user in the list
    setUserProfiles(prev =>
      prev.map(profile =>
        profile.UserProfileId === updatedUser.UserProfileId
          ? updatedUser
          : profile
      )
    )

    // Also update filtered profiles if they exist
    setFilteredProfiles(prev =>
      prev.map(profile =>
        profile.UserProfileId === updatedUser.UserProfileId
          ? updatedUser
          : profile
      )
    )
  }

  const handleCloseRoleEditModal = () => {
    setEditingUser(null)
    setIsRoleEditModalOpen(false)
  }

  const handleAddNewUser = () => {
    setIsCreateUserModalOpen(true)
  }

  const handleCreateUserSuccess = (newUser: UserProfileWithRolesDto) => {
    setIsCreateUserModalOpen(false)

    // Add the new user to the existing list instead of refreshing from server
    setUserProfiles(prev => [...prev, newUser])
    // Note: filteredProfiles will be automatically updated by the useEffect that depends on userProfiles
  }

  const handleCloseCreateUserModal = () => {
    setIsCreateUserModalOpen(false)
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 text-slate-400 dark:text-slate-500 mx-auto mb-4 animate-pulse" />
        <div className="text-slate-600 dark:text-slate-300">Loading users...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 max-w-md mx-auto">
          <div className="text-red-700 dark:text-red-400 mb-2">Error loading users</div>
          <div className="text-sm text-red-600 dark:text-red-300">{error}</div>
          <button
            onClick={loadUserRoles}
            className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with workspace selector, search, and add button */}
      <div className="flex items-center justify-between gap-4 mb-6">
        {/* Left side - Workspace selector */}
        <div className="flex items-center gap-3">
          {manageableWorkspaces.length > 0 && (
            <UserManagementWorkspaceSelector
              selectedWorkspaceId={selectedWorkspaceId}
              onWorkspaceChange={setSelectedWorkspaceId}
              manageableWorkspaces={manageableWorkspaces}
              isCompanyAdmin={isCompanyAdmin}
            />
          )}
        </div>

        {/* Right side - Search and Add button */}
        <div className="flex items-center gap-3">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search users, emails, or roles..."
              value={searchQuery}
              onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
              className="pl-10 pr-4 py-2 w-80 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Add user button */}
          <button
            onClick={handleAddNewUser}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        </div>
      </div>

      {/* Users list */}
      {filteredProfiles.length > 0 ? (
        <>
          {/* Results counter */}
          <div className="text-sm text-slate-600 dark:text-slate-400">
            {searchQuery ? (
              <>Showing {filteredProfiles.length} of {userProfiles.length} users</>
            ) : (
              <>{userProfiles.length} users total</>
            )}
          </div>

          <div className="space-y-1">
            {filteredProfiles.map((userProfile: UserProfileWithRolesDto) => (
              <UserRow
                key={userProfile.UserProfileId}
                userProfile={userProfile}
                workspaces={selectedCompany.Workspaces}
                onDeleteUser={handleDeleteUser}
                onEditRoles={handleEditUserRoles}
              />
            ))}
          </div>
        </>
      ) : userProfiles.length > 0 ? (
        <div className="text-center py-8">
          <Search className="h-8 w-8 text-slate-400 dark:text-slate-500 mx-auto mb-3" />
          <div className="text-slate-600 dark:text-slate-300 mb-2">No users found</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Try adjusting your search terms
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
          <div className="text-slate-600 dark:text-slate-300 mb-2">No users found</div>
          <div className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            No users have been assigned roles in {selectedCompany.Name} yet.
          </div>
          <button
            onClick={handleAddNewUser}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2 mx-auto"
          >
            <Plus className="w-4 h-4" />
            Add First User
          </button>
        </div>
      )}

      {/* Role Edit Modal */}
      {editingUser && (
        <UserRoleEditModal
          isOpen={isRoleEditModalOpen}
          user={editingUser}
          companyWorkspaces={selectedCompany.Workspaces || []}
          allUsers={userProfiles}
          onClose={handleCloseRoleEditModal}
          onSuccess={handleRoleEditSuccess}
        />
      )}

      {/* Create User Modal */}
      <UserCreateModal
        isOpen={isCreateUserModalOpen}
        selectedCompany={selectedCompany}
        onClose={handleCloseCreateUserModal}
        onSuccess={handleCreateUserSuccess}
      />

      {/* Delete User Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Remove User"
        message={`Are you sure you want to remove "${userToDelete?.name}" from this company? This will remove all their roles and access but will not delete their user account.`}
        type="warning"
        confirmText="Remove User"
        cancelText="Cancel"
        onConfirm={confirmDeleteUser}
        onCancel={cancelDeleteUser}
        loading={deleteLoading}
      />
    </div>
  )
}
